import React, { useContext, useEffect, useState } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';

import { t } from '@lingui/macro';
import { Dialog, DialogProps, Spinner } from '@economic/taco';
import { useLocalization } from '~/app/shared/components/l10n/l10n';

import { DimensionValue } from '~/app/shared/dimensions/types';

import RoutedDialog from '../components/RoutedDialog';
import CreateDimensionValueForm from '../forms/CreateDimensionValueForm';

import { OutletContext } from '../types';
import dialogLayerContext from '../context/dialogLayerContext';

const CreateDimensionValueDialog = () => {
    const { dimensionId } = useParams();
    const { context, clearContext } = useContext(dialogLayerContext);
    const { closeDialog } = useOutletContext<OutletContext>();
    const loadingTexts = useLocalization(textIds);

    const [initialData, setInitialData] = useState<Partial<DimensionValue>>({ dimensionId: Number(dimensionId), active: true });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (typeof context.duplicateDimensionValueDialog !== 'undefined') {
            setInitialData({ ...context.duplicateDimensionValueDialog, key: undefined });
        }

        setLoading(false);

        return () => clearContext('duplicateDimensionValueDialog');
    }, []);

    return (
        <RoutedDialog onClose={closeDialog} draggable>
            <Dialog.Content aria-label={t`Create new dimension value`}>
                {loading || loadingTexts ? (
                    <div className="flex flex-col items-center justify-center">
                        <Spinner />
                    </div>
                ) : (
                    <CreateDimensionValueForm
                        allowSaveAndNew
                        allowCreationOfBarred
                        onClose={closeDialog}
                        initialData={initialData}
                    />
                )}
            </Dialog.Content>
        </RoutedDialog>
    );
};

const textIds = ['ERROR_CODE_DimensionDoesNotExist', 'ERROR_CODE_KeyInUse'];

type CreateDimensionValueDialogProps = Partial<DialogProps> & {
    dimensionId: number;
    showSaveAndApply?: boolean;
    onValueCreatedCallback: (dimensionValue: DimensionValue) => void;
    onDialogClosedCallback?: () => void;
};
const UnroutedDialog = ({
    dimensionId,
    showSaveAndApply = false,
    onValueCreatedCallback,
    onDialogClosedCallback,
    ...props
}: CreateDimensionValueDialogProps) => {
    const loadingTexts = useLocalization(textIds);

    return (
        <Dialog {...props} size="xs" draggable>
            <Dialog.Content aria-label={t`Create new dimension value`}>
                {loadingTexts ? (
                    <Spinner />
                ) : (
                    <CreateDimensionValueForm
                        onSubmitted={onValueCreatedCallback}
                        onClose={() => {
                            onDialogClosedCallback?.();
                        }}
                        showSaveAndApply={showSaveAndApply}
                        initialData={{ dimensionId: Number(dimensionId), active: true }}
                    />
                )}
            </Dialog.Content>
        </Dialog>
    );
};

CreateDimensionValueDialog.Unrouted = UnroutedDialog;
export default CreateDimensionValueDialog;
